import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot, Pencil, Pin, PinOff, Search, SquarePen, Trash2 } from 'lucide-react'
import { Gespraech } from '@/components/assistent/Gespraech'
import { apiErrorText } from '@/lib/api-error'
import { assistentApi, type GespraechKopf } from '@/lib/assistent/assistent.api'
import { bestaetige, frage } from '@/lib/dialoge'
import { fmtDate } from '@/lib/format-date'
import { pageTitle } from '@/lib/form-styles'

const SEITE = 30

/**
 * AP-18 5.2 — Menüpunkt **„Assistent"**: links die eigenen Gespräche (suchen, anheften, umbenennen,
 * löschen), rechts das gewählte — dieselbe Fläche wie in der Seitenleiste, nur breiter. Ein
 * Gespräch aus der Seitenleiste lässt sich hier fortsetzen und umgekehrt.
 *
 * Hier gibt es keinen Ort: Ein Gespräch, das auf dieser Seite beginnt, gehört zu keinem Bereich.
 */
export default function AssistantPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const [liste, setListe] = useState<GespraechKopf[]>([])
  const [gesamt, setGesamt] = useState(0)
  const [seite, setSeite] = useState(1)
  const [suche, setSuche] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [geladen, setGeladen] = useState(false)
  const anfrage = useRef(0)

  const laden = useCallback(
    async (q: string, bis: number) => {
      const nummer = ++anfrage.current
      try {
        const r = await assistentApi.liste({ q: q.trim() || undefined, page: 1, limit: bis * SEITE > 100 ? 100 : bis * SEITE })
        // Nur die jüngste Antwort zählt — eine ältere Suche darf die neuere nicht überschreiben.
        if (nummer !== anfrage.current) return
        setListe(r.data)
        setGesamt(r.meta.total)
        setFehler(null)
      } catch (e) {
        if (nummer === anfrage.current) setFehler(apiErrorText(e, t('assistant.page.loadError')))
      } finally {
        if (nummer === anfrage.current) setGeladen(true)
      }
    },
    [t],
  )

  // Beim Tippen kurz warten, sonst fragt jede Taste.
  useEffect(() => {
    const uhr = setTimeout(() => void laden(suche, seite), suche ? 250 : 0)
    return () => clearTimeout(uhr)
  }, [suche, seite, laden])

  const neuLaden = useCallback(() => void laden(suche, seite), [laden, suche, seite])

  async function tu(schritt: () => Promise<unknown>) {
    try {
      await schritt()
      neuLaden()
    } catch (e) {
      setFehler(apiErrorText(e, t('assistant.page.actionError')))
    }
  }

  async function umbenennen(g: GespraechKopf) {
    const titel = await frage(t('assistant.page.renamePrompt'), g.title, { pflicht: true, ok: t('assistant.page.rename') })
    if (titel === null || titel.trim() === g.title) return
    await tu(() => assistentApi.aendern(g.id, { title: titel.trim() }))
  }

  async function loeschen(g: GespraechKopf) {
    if (!(await bestaetige(t('assistant.page.deleteConfirm', { title: g.title || t('assistant.newConversation') }), { ok: t('common.delete'), gefaehrlich: true }))) return
    await tu(() => assistentApi.loeschen(g.id))
    if (g.id === id) navigate('/assistant', { replace: true })
  }

  const knopf = 'rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground'
  return (
    <div className="flex h-full min-h-0 flex-col p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Bot size={24} className="text-primary" />
          <h1 className={pageTitle}>{t('assistant.title')}</h1>
        </div>
        <button type="button" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" onClick={() => navigate('/assistant')} disabled={!id}>
          <SquarePen className="h-4 w-4" /> {t('assistant.newConversation')}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* Gespräche */}
        <div className="flex max-h-56 flex-col rounded-lg border border-border bg-card lg:max-h-none lg:w-80 lg:flex-shrink-0">
          <div className="border-b border-border p-2">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <input
                type="search"
                className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm"
                placeholder={t('assistant.page.search')}
                aria-label={t('assistant.page.search')}
                value={suche}
                onChange={(e) => {
                  setSuche(e.target.value)
                  setSeite(1)
                }}
              />
            </label>
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto p-1">
            {liste.map((g) => (
              <li key={g.id} className={`group flex items-start gap-1 rounded-md px-2 py-1.5 ${g.id === id ? 'bg-accent' : 'hover:bg-accent/50'}`}>
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(`/assistant/${g.id}`)} aria-current={g.id === id ? 'true' : undefined}>
                  <span className="flex items-center gap-1 text-sm">
                    {g.pinned && <Pin className="h-3 w-3 flex-shrink-0 text-primary" aria-label={t('assistant.page.pinned')} />}
                    <span className="truncate">{g.title || t('assistant.newConversation')}</span>
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {fmtDate(g.lastMessageAt)}
                    {g.context?.label ? ` · ${g.context.label}` : ''}
                  </span>
                </button>
                <span className="flex flex-shrink-0 opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                  <button type="button" className={knopf} onClick={() => void tu(() => assistentApi.aendern(g.id, { pinned: !g.pinned }))} title={t(g.pinned ? 'assistant.page.unpin' : 'assistant.page.pin')} aria-label={t(g.pinned ? 'assistant.page.unpin' : 'assistant.page.pin')}>
                    {g.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </button>
                  <button type="button" className={knopf} onClick={() => void umbenennen(g)} title={t('assistant.page.rename')} aria-label={t('assistant.page.rename')}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" className={knopf} onClick={() => void loeschen(g)} title={t('common.delete')} aria-label={t('common.delete')}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </li>
            ))}
            {geladen && liste.length === 0 && <li className="px-2 py-3 text-sm text-muted-foreground">{suche.trim() ? t('assistant.page.noHits') : t('assistant.page.empty')}</li>}
            {liste.length < gesamt && liste.length < 100 && (
              <li className="p-1">
                <button type="button" className="w-full rounded-md border border-border px-2 py-1 text-xs hover:bg-accent" onClick={() => setSeite((s) => s + 1)}>
                  {t('assistant.page.more', { rest: gesamt - liste.length })}
                </button>
              </li>
            )}
          </ul>
          {fehler && <p className="border-t border-border px-3 py-2 text-xs text-destructive">{fehler}</p>}
        </div>

        {/* Gespräch */}
        <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card">
          <Gespraech gespraechId={id ?? null} onGespraech={(neu) => navigate(`/assistant/${neu}`, { replace: true })} onGeaendert={neuLaden} />
        </div>
      </div>
    </div>
  )
}
